import { useState, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Container } from '@/components/layout/Container'
import { FilterBar, type SortOption, type TagOption } from '@/components/fragrance/FilterBar'
import { ProductGrid } from '@/components/sections/ProductGrid'
import { useCatalogue } from '@/hooks/useCatalogue'

import type { ScentFamily } from '@/types'

interface FilterState {
  tag: TagOption
  families: ScentFamily[]
  concentration: string[]
  sort: SortOption
  priceMax: number | null
}

const tagTitle: Record<TagOption, string> = {
  all: 'The Collection',
  new: 'New Arrivals',
  bestseller: 'Bestsellers',
  'discovery-set': 'Discovery Sets',
  refill: 'Refills',
}

const tagSubtitle: Record<TagOption, string> = {
  all: 'Every fragrance is built around a clear scent idea: a place, a mood, a memory. Sample first, then commit.',
  new: 'The newest additions to the house.',
  bestseller: 'The fragrances our community returns to.',
  'discovery-set': 'Sample before you commit.',
  refill: 'Refills for the sizes you already love.',
}

export function Shop() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { products: apiProducts, loading } = useCatalogue()

  const [filters, setFilters] = useState<FilterState>({
    tag: (searchParams.get('tag') as TagOption) ?? 'all',
    families: (searchParams.get('family')?.split(',') as ScentFamily[])?.filter(Boolean) ?? [],
    concentration: [],
    sort: (searchParams.get('sort') as SortOption) ?? 'featured',
    priceMax: searchParams.get('max') ? parseInt(searchParams.get('max')!, 10) : null,
  })

  useEffect(() => {
    const next = new URLSearchParams()
    if (filters.tag !== 'all') next.set('tag', filters.tag)
    if (filters.families.length) next.set('family', filters.families.join(','))
    if (filters.sort !== 'featured') next.set('sort', filters.sort)
    if (filters.priceMax) next.set('max', filters.priceMax.toString())
    setSearchParams(next, { replace: true })
  }, [filters, setSearchParams])

  const perfumes = apiProducts

  const filtered = useMemo(() => {
    let result = [...perfumes]

    if (filters.tag !== 'all') {
      result = result.filter((p) => p.labels?.includes(filters.tag as 'new' | 'bestseller' | 'discovery-set' | 'refill'))
    }

    if (filters.families.length) {
      result = result.filter((p) => filters.families.includes(p.family))
    }

    if (filters.concentration.length) {
      result = result.filter((p) => filters.concentration.includes(p.concentration))
    }

    if (filters.priceMax) {
      result = result.filter((p) => p.sizes[0]?.price != null && p.sizes[0].price <= filters.priceMax!)
    }

    switch (filters.sort) {
      case 'price-asc':
        result.sort((a, b) => (a.sizes[0]?.price ?? 0) - (b.sizes[0]?.price ?? 0))
        break
      case 'price-desc':
        result.sort((a, b) => (b.sizes[0]?.price ?? 0) - (a.sizes[0]?.price ?? 0))
        break
      case 'newest':
        result.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0))
        break
    }

    return result
  }, [filters, perfumes])

  const title = tagTitle[filters.tag]
  const subtitle = tagSubtitle[filters.tag]

  return (
    <div className="pt-28 pb-24">
      <Container>
        <p className="mb-4 text-xs uppercase tracking-[0.2em] text-wd-orange">{title}</p>
        <h1 className="mb-4 font-display text-4xl normal-case text-carbon md:text-5xl">
          {filters.tag === 'all' ? 'fragrances' : filters.tag.replace('-', ' ')}
        </h1>
        <p className="mb-10 max-w-2xl text-carbon/60">{subtitle}</p>

        <FilterBar filters={filters} onChange={setFilters} />

        <div className="mt-10">
          {loading && filtered.length === 0 ? (
            <div className="py-20 text-center text-carbon/60">
              <p>Loading fragrances...</p>
            </div>
          ) : (
            <ProductGrid perfumes={filtered} />
          )}
        </div>
      </Container>
    </div>
  )
}
